import { Action, createReducer, on } from '@ngrx/store';
import { PermissionsManagerActions } from '../actions/actions-type.action';
import {
    PermissionGroupItem,
    PermissionGroupResponse,
} from '../../models/api/response/permissions-management.response';
import { IndividualPermissionsResponse } from '../../models/api/response/individual-permissions.response';
import { SummaryResponse } from '../../models/api/response/summary.response';

export interface PermissionsManagerState {
    permissionsGroups: PermissionGroupResponse;
    permissionsList: any[];
    individualPermissions: IndividualPermissionsResponse | null;
    summary: any;
    error: any;
    isLoading: boolean;
    isLoadingPermissions: boolean;
    isLoadingIndividualPermissions: boolean;
    isLoadingSummary: boolean;
    areUsersInGroupLoading: boolean;
    isUserToSuggestLoading: boolean;
    usersInCurrentGroup: any[];
}

const initialState: any = {
    error: null,
    permissionsGroups: [],
    permissionsList: [],
    individualPermissions: null,
    summary: null,
    isLoading: false,
    isLoadingPermissions: false,
    isLoadingIndividualPermissions: false,
    isLoadingSummary: false,
    areUsersInGroupLoading: false,
    isUserToSuggestLoading: false,
    usersInCurrentGroup: [],
};

export const reducer = createReducer(
    initialState,
    on(
        PermissionsManagerActions.fetchPermissionsGroupRequests,
        (state, action) => ({
            ...state,
            isLoading: true,
        })
    ),
    on(
        PermissionsManagerActions.fetchPermissionsGroupRequestsSuccess,
        (state, action) => {
            return {
                ...state,
                permissionsGroups: action.payload,
                isLoading: false,
            };
        }
    ),
    on(
        PermissionsManagerActions.fetchPermissionsGroupRequestsFailure,
        (state, action) => ({
            ...state,
            error: action.payload,
            isLoading: false,
        })
    ),
    on(PermissionsManagerActions.fetchPermissionsRequests, (state, action) => ({
        ...state,
        isLoadingPermissions: true,
    })),

    on(
        PermissionsManagerActions.fetchPermissionsRequestsSuccess,
        (state, action) => ({
            ...state,
            permissionsList: action.payload.items,
            isLoadingPermissions: false,
        })
    ),
    on(
        PermissionsManagerActions.fetchPermissionsRequestsFailure,
        (state, action) => ({
            ...state,
            error: action.payload,
            isLoadingPermissions: false,
        })
    ),
    on(PermissionsManagerActions.fetchUsersInGroup, (state, action) => ({
        ...state,
        areUsersInGroupLoading: true,
    })),
    on(PermissionsManagerActions.fetchUsersInGroupSuccess, (state, action) => {
        return {
            ...state,
            usersInCurrentGroup: action.payload,
            areUsersInGroupLoading: false,
        };
    }),
    on(PermissionsManagerActions.fetchUsersInGroupFailure, (state, action) => ({
        ...state,
        error: action.payload,
        areUsersInGroupLoading: false,
    })),
    on(
        PermissionsManagerActions.fetchIndividualPermissionsRequest,
        (state, action) => ({
            ...state,
            isLoadingIndividualPermissions: true,
        })
    ),
    on(
        PermissionsManagerActions.fetchIndividualPermissionsSuccess,
        (state, action) => ({
            ...state,
            individualPermissions: action.payload,
            isLoadingIndividualPermissions: false,
        })
    ),
    on(
        PermissionsManagerActions.fetchIndividualPermissionsFailure,
        (state, action) => ({
            ...state,
            error: action.payload,
            isLoadingIndividualPermissions: false,
        })
    ),
    on(PermissionsManagerActions.addPermissionsToGroup, (state, action) => ({
        ...state,
        isLoading: true,
    })),
    on(
        PermissionsManagerActions.removePermissionsGroupRequest,
        (state, action) => ({
            ...state,
            isLoading: true,
        })
    ),
    on(
        PermissionsManagerActions.removePermissionsGroupsSuccess,
        (state, action) => {
            const groupId = action.payload.id;
            return {
                ...state,
                permissionsGroups: {
                    items: {
                        items: state.permissionsGroups.items.items.filter(
                            (group: PermissionGroupItem) =>
                                group.groupId !== groupId
                        ),
                    },
                },
                isLoading: false,
            };
        }
    ),
    on(
        PermissionsManagerActions.addPermissionsToGroupSuccess,
        (state, action) => {
            const groupId = action.payload.items.groupId;
            const updatedPermissionsGroups =
                state.permissionsGroups.items.items.map(
                    (group: PermissionGroupItem) => {
                        if (group.groupId === groupId) {
                            return {
                                ...group,
                                permissions:
                                    action.payload.items.permissions ||
                                    group.permissions,
                            };
                        }
                        return group;
                    }
                );
            return {
                ...state,
                permissionsGroups: {
                    items: { items: updatedPermissionsGroups },
                },
                isLoading: false,
            };
        }
    ),
    on(
        PermissionsManagerActions.addPermissionsToGroupFailure,
        (state, action) => ({
            ...state,
            error: action.payload,
            isLoading: false,
        })
    ),
    on(
        PermissionsManagerActions.deletePermissionsFromGroup,
        (state, action) => ({
            ...state,
            isLoading: true,
        })
    ),
    on(
        PermissionsManagerActions.deletePermissionsFromGroupSuccess,
        (state, action) => {
            const groupId = action.payload.items.groupId;
            const permissionIds = action.payload.items.permissionIds || [];

            const updatedPermissionsGroups =
                state.permissionsGroups.items.items.map(
                    (group: PermissionGroupItem) => {
                        if (group.groupId === groupId) {
                            return {
                                ...group,
                                permissions: group.permissions.filter(
                                    (permission) =>
                                        !permissionIds.includes(
                                            permission.permissionId
                                        )
                                ),
                            };
                        }
                        return group;
                    }
                );

            return {
                ...state,
                permissionsGroups: {
                    items: { items: updatedPermissionsGroups },
                },
                isLoading: false,
            };
        }
    ),
    on(
        PermissionsManagerActions.deletePermissionsFromGroupFailure,
        (state, action) => ({
            ...state,
            error: action.payload,
            isLoading: false,
        })
    ),
    on(
        PermissionsManagerActions.updatePermissionsGroupRequest,
        (state, action) => ({
            ...state,
            isLoading: true,
        })
    ),
    on(
        PermissionsManagerActions.updatePermissionsGroupSuccess,
        (state, action) => {
            const updatedGroup = action.payload.items;
            const updatedPermissionsGroups =
                state.permissionsGroups.items.items.map(
                    (group: PermissionGroupItem) => {
                        if (group.groupId === updatedGroup.id) {
                            return {
                                ...group,
                                ...updatedGroup,
                            };
                        }
                        return group;
                    }
                );

            return {
                ...state,
                permissionsGroups: {
                    items: { items: updatedPermissionsGroups },
                },
                isLoading: false,
            };
        }
    ),

    on(
        PermissionsManagerActions.updatePermissionsGroupFailure,
        (state, action) => ({
            ...state,
            error: action.payload,
            isLoading: false,
        })
    ),
    on(
        PermissionsManagerActions.addNewPermissionsGroupRequest,
        (state, action) => ({
            ...state,
            isLoading: true,
        })
    ),
    on(
        PermissionsManagerActions.addNewPermissionsGroupsSuccess,
        (state, action) => {
            return {
                ...state,
                permissionsGroups: {
                    items: {
                        items: [
                            ...state.permissionsGroups.items.items,
                            ...[action.payload.newGroup],
                        ],
                    },
                },
                isLoading: false,
            };
        }
    ),
    on(
        PermissionsManagerActions.addNewPermissionsGroupFailure,
        (state, action) => ({
            ...state,
            error: action.payload,
            isLoading: false,
        })
    ),
    on(
        PermissionsManagerActions.duplicatePermissionGroupRequest,
        (state, action) => ({
            ...state,
            isLoading: true,
        })
    ),
    on(
        PermissionsManagerActions.duplicatePermissionGroupSuccess,
        (state, action) => {
            return {
                ...state,
                permissionsGroups: {
                    items: {
                        items: [
                            ...state.permissionsGroups.items.items,
                            ...[action.payload.newGroup],
                        ],
                    },
                },
                isLoading: false,
            };
        }
    ),
    on(
        PermissionsManagerActions.duplicatePermissionGroupFailure,
        (state, action) => ({
            ...state,
            error: action.payload,
            isLoading: false,
        })
    ),
    on(PermissionsManagerActions.fetchSummaryRequest, (state, action) => ({
        ...state,
        isLoadingSummary: true,
    })),
    on(PermissionsManagerActions.fetchSummarySuccess, (state, action) => ({
        ...state,
        summary: action.payload,
        isLoadingSummary: false,
    })),
    on(PermissionsManagerActions.fetchSummaryFailure, (state, action) => ({
        ...state,
        error: action.payload,
        summary: [],
        isLoadingSummary: false,
    }))
);
export const permissionsManagerReducer = (state: any, action: Action) =>
    reducer(state, action);
